import { UserInfoType } from '@Entities/schemas/user/user-info-schema'
import { UserInfo } from '@Entities/user/user'
import { utcToLocalTzWithoutTimeChange } from '@App/helpers/utc-to-local-tz-without-time-change'
import dayjs from 'dayjs'
import { omit } from 'lodash'

const buildFullPhoneNumber = (userInfoData: UserInfo): string => {
    const { phoneCountryCode, phoneAreaCode, phoneNumber, fullPhoneNumber } = userInfoData

    if (fullPhoneNumber) {
        const normalized = fullPhoneNumber.trim()

        // If persisted as E.164, strip the dial code so the form stores only the national part.
        if (phoneCountryCode && normalized.startsWith(`+${phoneCountryCode}`)) {
            return normalized.slice(String(phoneCountryCode).length + 1)
        }

        return normalized.replace(/^\+/, '')
    }

    if (!phoneCountryCode) {
        return ''
    }

    return `${phoneAreaCode ?? ''}${phoneNumber ?? ''}`
}

export const userinfoToZodMapper = (userInfoData: UserInfo): UserInfoType => {
    // Destructure old phone fields to exclude them from the spread below; they are mapped via buildFullPhoneNumber
    const { phoneCountryCode, ...withoutLegacyPhoneFields } = omit(userInfoData, ['phoneAreaCode', 'phoneNumber'])
    const { streetAddress, unitNumber, city, state, postalCode, countryId, ...withoutLegacyPhoneAndAddress } =
        withoutLegacyPhoneFields

    const address = {
        streetAddress,
        unitNumber: unitNumber ? unitNumber : '',
        city,
        state: state ? state : '',
        postalCode: postalCode ? postalCode : '',
        countryId: countryId ? String(countryId) : '',
    }
    const phoneNumber = {
        phoneCountryCode: phoneCountryCode ? String(phoneCountryCode) : '',
        fullPhoneNumber: buildFullPhoneNumber(userInfoData),
    }

    const src = withoutLegacyPhoneFields.settings
    const retypedSettings: UserInfoType['settingsForm'] = src
        ? {
              privacyPolicy: src.privacyPolicy,
              termsAndConditions: src.termsAndConditions,
              searchTermsAndConditions: src.searchTermsAndConditions,
              showWelcomePopup: src.showWelcomePopup,
              mfaBySms: src.mfaBySms,
              mfaByEmail: src.mfaByEmail,
              showDetailedWorkCards: src.showDetailedWorkCards,
          }
        : undefined

    // Conversion from open-string field from API, to string-literal Zod type
    const retypedGroups = withoutLegacyPhoneFields.groups as unknown as UserInfoType['groups']
    return {
        ...withoutLegacyPhoneAndAddress,
        groups: retypedGroups,
        settingsForm: retypedSettings,
        username: withoutLegacyPhoneAndAddress.email,
        birthDate: withoutLegacyPhoneAndAddress.birthDate
            ? utcToLocalTzWithoutTimeChange(withoutLegacyPhoneAndAddress.birthDate)
            : dayjs().subtract(13, 'year').toDate(),
        address,
        phoneNumber,
    }
}
